import {
  ConversationMessageEntity,
  ConversationSystemMeta,
} from '../../../types/conversation.types';

/**
 * Membership and metadata changes, as sentences.
 *
 * The backend writes these into the thread as `side: 'system'` rows carrying the
 * event and its operands but no `body` - the wording is the client's, so it can
 * say "You" to the person who did it.
 */

/** "Ana", or "You" when the actor is the person reading. */
const actorName = (
  meta: ConversationSystemMeta | null,
  viewerId: number | null
) => {
  if (!meta) return 'Someone';
  if (viewerId !== null && meta.byUserId === viewerId) return 'You';
  return meta.byName || 'Someone';
};

/** "Ana", "Ana and Ivan", "Ana, Ivan and Marko". */
const joinNames = (names: string[]) => {
  if (names.length === 0) return 'someone';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/**
 * Returns an empty string for an event we do not recognise, which the caller
 * treats as "render nothing" rather than printing a raw enum at someone.
 */
export const systemMessageText = (
  message: ConversationMessageEntity,
  viewerId: number | null
): string => {
  const meta = message.systemMeta;
  const by = actorName(meta, viewerId);

  switch (message.systemEvent) {
    case 'groupCreated':
      return meta?.title
        ? `${by} created the group "${meta.title}"`
        : `${by} created the group`;

    case 'participantsAdded':
      return `${by} added ${joinNames(
        (meta?.users ?? []).map((user) =>
          viewerId !== null && user.id === viewerId ? 'you' : user.name
        )
      )}`;

    case 'participantRemoved':
      return `${by} removed ${
        viewerId !== null && meta?.userId === viewerId
          ? 'you'
          : meta?.name || 'a member'
      }`;

    case 'participantLeft':
      return `${
        viewerId !== null && meta?.userId === viewerId
          ? 'You'
          : meta?.name || 'A member'
      } left the group`;

    case 'titleChanged':
      return meta?.title
        ? `${by} renamed the group to "${meta.title}"`
        : `${by} renamed the group`;

    case 'imageChanged':
      return `${by} changed the group photo`;

    /* A dispatcher thread. Admin only: the customer is never sent it. */
    case 'handedToHuman': {
      const who =
        meta?.by === 'customer'
          ? 'The customer asked for a person'
          : 'The AI handed this to a person';
      return meta?.reason ? `${who}: ${meta.reason}` : who;
    }

    default:
      // A body is the older shape, and still what a non-group system message
      // (if the backend ever adds one) would carry.
      return message.body ?? '';
  }
};
